/**
 * Authentication Service
 * Centralized service for handling all authentication-related operations
 */

import apiClient from './axios';

// Validate apiClient import to prevent minification issues
if (!apiClient || typeof apiClient !== 'function') {
  throw new Error('API client not properly loaded');
}

class AuthService {
  /**
   * Login with email and password
   * @param {Object} credentials - Login credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   * @returns {Promise<Object>} Login response
   */
  async login(credentials) {
    try {
      const response = await apiClient.post('/auth/login', credentials);

      if (response.accessToken) {
        // Store tokens and user data
        localStorage.setItem('auth_token', response.accessToken);
        localStorage.setItem('mgx_token', response.accessToken);
        sessionStorage.setItem('auth_token', response.accessToken);
        sessionStorage.setItem('mgx_token', response.accessToken);
        if (response.refreshToken) {
          localStorage.setItem('refresh_token', response.refreshToken);
        }
        localStorage.setItem('mgx_user', JSON.stringify(response.user));
        localStorage.setItem('mgx_user_type', response.user?.role || 'user');
      }

      return response;
    } catch (error) {
      // Clear auth data on failed login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('mgx_user');
      localStorage.removeItem('mgx_user_type');
      throw error;
    }
  }

  /**
     * Register new user
     * NOTE: Registration no longer performs auto-login.
     * Users must complete the normal login (with OTP) after registering.
     * @param {Object} userData - User registration data
     * @returns {Promise<Object>} Registration response
     */
  async register(userData) {
    try {
      const response = await apiClient.post('/auth/register', userData);
      // Do NOT store tokens or user data here – backend no longer returns tokens on register.
      return response;
    } catch (error) {
      // Clear any partial auth data on failed registration just in case
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('mgx_user');
      localStorage.removeItem('mgx_user_type');
      throw error;
    }
  }

  /**
   * Logout current user
   * @returns {Promise<Object>} Logout response
   */
  async logout() {
    try {
      const response = await apiClient.post('/auth/logout');
      // Clear local auth data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('mgx_user');
      localStorage.removeItem('mgx_user_type');
      return response;
    } catch (error) {
      // Even if logout fails on server, clear local data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('mgx_user');
      localStorage.removeItem('mgx_user_type');
      console.warn('Logout warning:', error.message);
      return { success: true };
    }
  }

  /**
   * Refresh authentication token
   * @returns {Promise<Object>} Token refresh response
   */
  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiClient.post('/auth/refresh-token', { refreshToken });

      if (response.accessToken) {
        // Store new tokens
        localStorage.setItem('auth_token', response.accessToken);
        if (response.refreshToken) {
          localStorage.setItem('refresh_token', response.refreshToken);
        }
      }

      return response;
    } catch (error) {
      // Clear auth data on failed token refresh
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('mgx_user');
      localStorage.removeItem('mgx_user_type');
      throw error;
    }
  }

  /**
   * Send OTP to phone number
   * @param {Object} contactInfo - Contact information
   * @param {string} contactInfo.phone - Phone number
   * @param {string} contactInfo.type - Contact type ('phone')
   * @returns {Promise<Object>} OTP send response
   */
  async sendOTP(contactInfo) {
    try {
      const response = await apiClient.post('/auth/send-otp', contactInfo);
      return { success: !!response?.success || true, message: response?.message || 'OTP sent successfully' };
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to send OTP' };
    }
  }

  /**
   * Validate credentials (email + password) without logging in
   * Used by email OTP flow to ensure password is correct BEFORE sending OTP
   * @param {Object} credentials - Login credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   * @returns {Promise<Object>} Validation response
   */
  async validateCredentials(credentials) {
    try {
      const response = await apiClient.post('/auth/validate-credentials', credentials);
      return {
        valid: response.valid || false,
        message: response.message || '',
        error: response.error || null
      };
    } catch (error) {
      return {
        valid: false,
        error: error?.data?.error || error?.message || 'Failed to validate credentials'
      };
    }
  }

  /**
   * Send OTP to email
   * @param {Object} contactInfo - Contact information
   * @param {string} contactInfo.email - Email address
   * @param {string} contactInfo.type - Contact type ('email')
   * @returns {Promise<Object>} OTP send response
   */
  async sendEmailOTP(contactInfo) {
    try {
      const response = await apiClient.post('/auth/send-otp', contactInfo);
      return { success: !!response?.success || true, message: response?.message || 'OTP sent successfully' };
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to send OTP' };
    }
  }

  /**
   * Verify OTP
   * @param {Object} otpData - OTP verification data
   * @param {string} otpData.otp - OTP code
   * @param {string} [otpData.email] - Email address (optional)
   * @param {string} [otpData.phone] - Phone number (optional)
   * @returns {Promise<Object>} OTP verification response
   */
  async verifyOTP(otpData) {
    try {
      const response = await apiClient.post('/auth/verify-otp', otpData);

      if (response.accessToken) {
        localStorage.setItem('auth_token', response.accessToken);
        localStorage.setItem('mgx_token', response.accessToken);
        sessionStorage.setItem('auth_token', response.accessToken);
        sessionStorage.setItem('mgx_token', response.accessToken);
        if (response.refreshToken) {
          localStorage.setItem('refresh_token', response.refreshToken);
        }
        if (response.user) {
          localStorage.setItem('mgx_user', JSON.stringify(response.user));
          localStorage.setItem('mgx_user_type', response.user?.role || 'user');
        }
      }

      // Mark success explicitly so callers can inspect it if needed
      return { success: true, ...response };
    } catch (error) {
      // Let callers see the real backend message (e.g. "Invalid OTP", "OTP has expired")
      const message = error?.data?.error || error?.data?.message || error?.message || 'Invalid OTP';
      const err = new Error(message);
      err.status = error.status || 400;
      err.data = error.data;
      throw err;
    }
  }

  /**
     * Firebase phone login: exchange Firebase ID token for MGX JWT
     * @param {Object} payload
     * @param {string} payload.idToken - Firebase ID token
     * @returns {Promise<Object>} Login response
     */
  async firebasePhoneLogin({ idToken }) {
    try {
      const response = await apiClient.post('/auth/firebase-phone-login', { idToken });

      if (response.accessToken) {
        localStorage.setItem('auth_token', response.accessToken);
        localStorage.setItem('mgx_token', response.accessToken);
        sessionStorage.setItem('auth_token', response.accessToken);
        sessionStorage.setItem('mgx_token', response.accessToken);
        if (response.refreshToken) {
          localStorage.setItem('refresh_token', response.refreshToken);
        }
        if (response.user) {
          localStorage.setItem('mgx_user', JSON.stringify(response.user));
          localStorage.setItem('mgx_user_type', response.user?.role || 'user');
        }
      }

      return { success: true, ...response };
    } catch (error) {
      const message = error?.data?.error || error?.data?.message || error?.message || 'Firebase phone login failed';
      const err = new Error(message);
      err.status = error.status || 400;
      err.data = error.data;
      throw err;
    }
  }

  /**
   * Firebase social login: exchange Firebase ID token (Google/Facebook/etc.) for MGX JWT
   * @param {Object} payload
   * @param {string} payload.idToken - Firebase ID token
   * @param {string} [payload.provider] - Social provider id (optional)
   * @returns {Promise<Object>} Login response
   */
  async firebaseSocialLogin({ idToken, provider }) {
    try {
      const response = await apiClient.post('/auth/firebase-social-login', { idToken, provider });

      if (response.accessToken) {
        localStorage.setItem('auth_token', response.accessToken);
        localStorage.setItem('mgx_token', response.accessToken);
        sessionStorage.setItem('auth_token', response.accessToken);
        sessionStorage.setItem('mgx_token', response.accessToken);
        if (response.refreshToken) {
          localStorage.setItem('refresh_token', response.refreshToken);
        }
        if (response.user) {
          localStorage.setItem('mgx_user', JSON.stringify(response.user));
          localStorage.setItem('mgx_user_type', response.user?.role || 'user');
        }
      }

      return { success: true, ...response };
    } catch (error) {
      const message = error?.data?.error || error?.data?.message || error?.message || 'Firebase social login failed';
      const err = new Error(message);
      err.status = error.status || 400;
      err.data = error.data;
      throw err;
    }
  }

  /**
   * Get current user profile
   * @returns {Promise<Object>} User profile data
   */
  async getProfile() {
    try {
      const response = await apiClient.get('/auth/profile');
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update user profile
   * @param {Object} profileData - Profile update data
   * @returns {Promise<Object>} Profile update response
   */
  async updateProfile(profileData) {
    try {
      const response = await apiClient.put('/auth/profile', profileData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Change user password
   * @param {Object} passwordData - Password change data
   * @param {string} passwordData.currentPassword - Current password
   * @param {string} passwordData.newPassword - New password
   * @returns {Promise<Object>} Password change response
   */
  async changePassword(passwordData) {
    try {
      const response = await apiClient.post('/auth/change-password', passwordData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Start forgot-password flow by sending a reset OTP to the user's email
   * @param {Object|string} payload - Email string or { email }
   * @returns {Promise<{success: boolean, message?: string, error?: string}>}
   */
  async forgotPassword(payload) {
    try {
      const email = typeof payload === 'string' ? payload : payload?.email;
      const response = await apiClient.post('/auth/forgot-password', { email });
      return {
        success: !!response?.success || true,
        message: response?.message || 'If an account exists for this email, an OTP has been sent.',
      };
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to start password reset' };
    }
  }

  /**
   * Complete password reset using email + OTP + new password
   * @param {Object} payload
   * @param {string} payload.email
   * @param {string} payload.otp
   * @param {string} payload.password - New password
   * @returns {Promise<{success: boolean, message?: string, error?: string}>}
   */
  async resetPassword(payload) {
    try {
      const { email, otp, password } = payload || {};
      const response = await apiClient.post('/auth/reset-password', { email, otp, password });
      return {
        success: !!response?.success || true,
        message: response?.message || 'Password reset successful. You can now login with your new password.',
      };
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to reset password' };
    }
  }

  /**
   * Send default OTP for admin users
   * @param {string} email - Admin email
   * @returns {Promise<Object>} Default OTP send response
   */
  async sendDefaultOTPAdmin(email) {
    try {
      const response = await apiClient.post('/auth/send-default-otp-admin', { email });
      return { success: !!response?.success || true, message: response?.message || 'Default admin OTP sent' };
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to send admin OTP' };
    }
  }

  /**
   * Admin login as user (impersonation)
   * @param {string} userId - Target user ID to impersonate
   * @returns {Promise<Object>} Login response with user tokens
   */
  async adminLoginAsUser(userId) {
    try {
      const response = await apiClient.post('/auth/admin-login-as-user', { userId });

      if (response.accessToken) {
        // Store tokens and user data for the impersonated user
        localStorage.setItem('auth_token', response.accessToken);
        localStorage.setItem('mgx_token', response.accessToken);
        sessionStorage.setItem('auth_token', response.accessToken);
        sessionStorage.setItem('mgx_token', response.accessToken);
        if (response.refreshToken) {
          localStorage.setItem('refresh_token', response.refreshToken);
        }
        if (response.user) {
          localStorage.setItem('mgx_user', JSON.stringify(response.user));
          localStorage.setItem('mgx_user_type', response.user?.role || 'user');
        }
      }

      return { success: true, ...response };
    } catch (error) {
      const message = error?.data?.error || error?.data?.message || error?.message || 'Failed to login as user';
      const err = new Error(message);
      err.status = error.status || 400;
      err.data = error.data;
      throw err;
    }
  }
}

// Export singleton instance
export const authService = new AuthService();

// Export individual methods for backward compatibility
export default {
  login: authService.login.bind(authService),
  validateCredentials: authService.validateCredentials.bind(authService),
  register: authService.register.bind(authService),
  logout: authService.logout.bind(authService),
  refreshToken: authService.refreshToken.bind(authService),
  sendOTP: authService.sendOTP.bind(authService),
  sendEmailOTP: authService.sendEmailOTP.bind(authService),
  verifyOTP: authService.verifyOTP.bind(authService),
  getProfile: authService.getProfile.bind(authService),
  updateProfile: authService.updateProfile.bind(authService),
  changePassword: authService.changePassword.bind(authService),
  forgotPassword: authService.forgotPassword.bind(authService),
  resetPassword: authService.resetPassword.bind(authService),
  sendDefaultOTPAdmin: authService.sendDefaultOTPAdmin.bind(authService),
  firebasePhoneLogin: authService.firebasePhoneLogin.bind(authService),
  firebaseSocialLogin: authService.firebaseSocialLogin.bind(authService),
  adminLoginAsUser: authService.adminLoginAsUser.bind(authService)
};
